import classNames from "classnames";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import React from "react";
import { OrgPositionData, OrgStaffSlot } from "../types";
import { SelectField } from "./fields";
import { CloseIcon } from "./icons";
import styles from "./OrgTree.module.scss";

/** Строка штата в правке: число ставок держим строкой, пока его набирают */
export interface StaffRow {
    key: string;
    positionId: string;
    gradeGuid: string;
    count: string;
}

export const toRows = (staff: OrgStaffSlot[]): StaffRow[] =>
    staff.map((s) => ({
        key: s.positionId,
        positionId: s.positionId,
        gradeGuid: s.gradeGuid ?? "",
        count: String(s.count),
    }));

/** Строки → штат для сохранения; null — в строках ошибка */
export function toStaff(rows: StaffRow[], positions: Map<string, OrgPositionData>): OrgStaffSlot[] | null {
    const result: OrgStaffSlot[] = [];
    for (const row of rows) {
        const position = positions.get(row.positionId);
        const count = Number(row.count);
        if (!position || !Number.isInteger(count) || count < 1 || count > 999) {
            return null;
        }
        if (result.some((s) => s.positionId === row.positionId)) {
            return null;
        }
        const grade = position.grades.find((g) => g.guid === row.gradeGuid);
        result.push({
            positionId: position.id,
            positionName: position.name,
            gradeGuid: grade?.guid ?? null,
            gradeName: grade?.name ?? null,
            count,
        });
    }
    return result;
}

export const sameStaff = (a: OrgStaffSlot[], b: OrgStaffSlot[]) =>
    a.length === b.length &&
    a.every(
        (s, i) =>
            s.positionId === b[i].positionId && (s.gradeGuid ?? "") === (b[i].gradeGuid ?? "") && s.count === b[i].count
    );

interface StaffEditorProps {
    rows: StaffRow[];
    positions: OrgPositionData[];
    /** действующих сотрудников подразделения по должностям */
    occupied: Map<string, number>;
    edit: boolean;
    onChange(rows: StaffRow[]): void;
    /** открыть справочник должностей (кнопка у списка должности); нет — без кнопки */
    onOpenPositions?(): void;
}

/**
 * Штат в карточке подразделения: должность, ранг (только для подписи вакансии)
 * и число ставок. Рядом — сколько ставок занято сотрудниками с этой должностью.
 */
export const StaffEditor = ({ rows, positions, occupied, edit, onChange, onOpenPositions }: StaffEditorProps) => {
    const byId = new Map(positions.map((p) => [p.id, p]));
    const used = new Set(rows.map((r) => r.positionId));
    const update = (key: string, patch: Partial<StaffRow>) =>
        onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));

    if (!edit && rows.length === 0) {
        return <div className={styles.hint}>Штат не задан</div>;
    }

    return (
        <div className={styles.staff}>
            {rows.map((row) => {
                const position = byId.get(row.positionId);
                const count = Number(row.count);
                const taken = occupied.get(row.positionId) ?? 0;
                const bad = row.count !== "" && (!Number.isInteger(count) || count < 1 || count > 999);
                const free = Number.isInteger(count) ? count - taken : 0;

                return (
                    <div key={row.key} className={styles.staffRow} data-staff-key={row.key}>
                        <div className={classNames(styles.staffMain, { [styles.withOpen]: edit && onOpenPositions })}>
                            {edit ? (
                                <SelectField
                                    value={row.positionId}
                                    filterable
                                    options={positions
                                        .filter((p) => p.id === row.positionId || !used.has(p.id))
                                        .map((p) => ({ id: p.id, label: p.name }))}
                                    emptyLabel={row.positionId ? undefined : "Должность"}
                                    onChange={(positionId) => update(row.key, { positionId, gradeGuid: "" })}
                                />
                            ) : (
                                <span className={styles.staffName}>
                                    {[position?.name, position?.grades.find((g) => g.guid === row.gradeGuid)?.name]
                                        .filter(Boolean)
                                        .join(" · ")}
                                </span>
                            )}
                            {edit && onOpenPositions && (
                                <PlanrButton
                                    type="graybtn"
                                    size="tiny"
                                    round
                                    icon="general-edit"
                                    className={styles.staffOpen}
                                    title="Справочник должностей"
                                    onClick={onOpenPositions}
                                />
                            )}
                            {edit ? (
                                <input
                                    className={classNames(styles.staffCount, { [styles.invalid]: bad || !row.count })}
                                    value={row.count}
                                    inputMode="numeric"
                                    aria-label="Ставок"
                                    title="Ставок"
                                    onChange={(e) => update(row.key, { count: e.target.value.replace(/[^\d]/g, "") })}
                                />
                            ) : (
                                <span className={styles.staffCountText}>{row.count}</span>
                            )}
                            {edit && (
                                <button
                                    type="button"
                                    className={styles.staffRemove}
                                    title="Убрать из штата"
                                    aria-label="Убрать из штата"
                                    onClick={() => onChange(rows.filter((r) => r.key !== row.key))}
                                >
                                    <CloseIcon />
                                </button>
                            )}
                        </div>
                        <div className={styles.staffSub}>
                            {edit && position && position.grades.length > 0 && (
                                <div className={styles.staffGrade}>
                                    <SelectField
                                        value={row.gradeGuid}
                                        emptyLabel="Без ранга"
                                        options={position.grades.map((g) => ({ id: g.guid, label: g.name }))}
                                        onChange={(gradeGuid) => update(row.key, { gradeGuid })}
                                    />
                                </div>
                            )}
                            {position && (
                                <span
                                    className={classNames(styles.staffState, {
                                        [styles.vacant]: free > 0,
                                        [styles.over]: free < 0,
                                    })}
                                >
                                    занято {Math.min(taken, Math.max(count, 0) || 0)} из{" "}
                                    {Number.isInteger(count) ? count : "—"}
                                    {free > 0 && ` · ${free} своб.`}
                                    {free < 0 && ` · ${-free} сверх штата`}
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
